import { useEffect, useState, useCallback } from "react";
import { Sparkles, Trash2, MessageSquareText, Copy, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { adminApi } from "../../lib/api";
import { inputCls, labelCls } from "./shared";

const STATUSES = ["new", "message_ready", "contacted", "joined", "declined"];
const STATUS_COLORS = {
  new: "bg-slate-100 text-slate-600",
  message_ready: "bg-blue-100 text-blue-700",
  contacted: "bg-amber-100 text-amber-700",
  joined: "bg-green-100 text-green-700",
  declined: "bg-red-100 text-red-700",
};

export function RecruitPanel() {
  const [items, setItems] = useState([]);
  const [raw, setRaw] = useState("");
  const [channel, setChannel] = useState("whatsapp");
  const [busyId, setBusyId] = useState(null);
  const [viewing, setViewing] = useState(null);

  const load = useCallback(() => {
    adminApi().get("/admin/prospects").then((r) => setItems(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const addProspects = async () => {
    const prospects = raw.split("\n").map((line) => {
      const parts = line.split("|").map((s) => s.trim());
      if (!parts[0]) return null;
      return { name: parts[0], contact: parts[1] || "", platform: parts[2] || "", audience: parts[3] || "", channel };
    }).filter(Boolean);
    if (prospects.length === 0) { toast.error("Add at least one prospect line"); return; }
    try {
      const { data } = await adminApi().post("/admin/prospects", { prospects });
      toast.success(`Added ${data.added} prospect${data.added === 1 ? "" : "s"}`);
      setRaw("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Add failed");
    }
  };

  const update = async (id, patch) => {
    try {
      await adminApi().put(`/admin/prospects/${id}`, patch);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
  };

  const generate = async (p) => {
    setBusyId(p.id);
    try {
      const { data } = await adminApi().post(`/admin/prospects/${p.id}/generate`);
      toast.success("Outreach message generated");
      setViewing(data);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Generation failed");
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete prospect ${p.name}?`)) return;
    await adminApi().delete(`/admin/prospects/${p.id}`);
    toast.success("Deleted");
    load();
  };

  const copyMsg = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  const waLink = (p) => {
    const digits = (p.contact || "").replace(/[^0-9]/g, "");
    if (p.channel !== "whatsapp" || digits.length < 8) return null;
    return `https://wa.me/${digits}?text=${encodeURIComponent(p.outreach_message)}`;
  };

  return (
    <div className="space-y-6">
      <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid="recruit-add-panel">
        <p className="font-heading font-bold text-slate-900 mb-1">Add prospects</p>
        <p className="text-xs text-slate-500 mb-3">
          One per line: <span className="font-mono bg-slate-50 px-1 rounded">Name | contact | platform | audience</span> — e.g.{" "}
          <span className="font-mono bg-slate-50 px-1 rounded">Ali Raza | +923001234567 | Facebook group | Overseas Pakistanis in Dubai</span>
        </p>
        <textarea
          data-testid="recruit-bulk-input"
          className={`${inputCls} h-24 font-mono text-xs`}
          placeholder={"Ali Raza | +923001234567 | Facebook group | Overseas Pakistanis in Dubai\nSana Travels Vlog | @sanatravels | Instagram | Umrah travel tips"}
          value={raw}
          onChange={(e) => setRaw(e.target.value)}
        />
        <div className="flex items-center gap-3 mt-3">
          <div>
            <label className={labelCls}>Outreach channel</label>
            <select data-testid="recruit-channel-select" className={inputCls} value={channel} onChange={(e) => setChannel(e.target.value)}>
              <option value="whatsapp">WhatsApp</option>
              <option value="email">Email</option>
              <option value="dm">Social DM</option>
            </select>
          </div>
          <button
            data-testid="recruit-add-btn"
            onClick={addProspects}
            className="mt-5 bg-[#970C10] hover:bg-[#7A0A0D] text-white font-bold text-sm px-5 py-2 rounded-lg transition-colors duration-200"
          >
            Add to pipeline
          </button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
        <table className="w-full text-sm min-w-[820px]">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3">Prospect</th>
              <th className="px-4 py-3">Platform / Audience</th>
              <th className="px-4 py-3">Channel</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Message</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.id} className="border-t border-slate-100 hover:bg-slate-50/70 transition-colors duration-150" data-testid={`recruit-row-${p.id}`}>
                <td className="px-4 py-3">
                  <p className="font-semibold text-slate-900">{p.name}</p>
                  <p className="text-xs text-slate-500 font-mono">{p.contact || "—"}</p>
                </td>
                <td className="px-4 py-3 text-xs text-slate-600">
                  {p.platform && <span className="block font-semibold">{p.platform}</span>}
                  {p.audience || "—"}
                </td>
                <td className="px-4 py-3">
                  <select
                    data-testid={`recruit-channel-${p.id}`}
                    className="text-xs border border-slate-200 rounded px-2 py-1"
                    value={p.channel}
                    onChange={(e) => update(p.id, { channel: e.target.value })}
                  >
                    <option value="whatsapp">WhatsApp</option>
                    <option value="email">Email</option>
                    <option value="dm">DM</option>
                  </select>
                </td>
                <td className="px-4 py-3">
                  <select
                    data-testid={`recruit-status-${p.id}`}
                    className={`text-xs font-bold rounded-full px-2 py-1 border-0 ${STATUS_COLORS[p.status] || STATUS_COLORS.new}`}
                    value={p.status}
                    onChange={(e) => update(p.id, { status: e.target.value })}
                  >
                    {STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
                  </select>
                </td>
                <td className="px-4 py-3">
                  {p.outreach_message ? (
                    <button
                      data-testid={`recruit-view-msg-${p.id}`}
                      onClick={() => setViewing(p)}
                      className="inline-flex items-center gap-1 text-xs font-bold text-[#970C10] hover:underline"
                    >
                      <MessageSquareText className="w-3.5 h-3.5" /> View
                    </button>
                  ) : (
                    <span className="text-xs text-slate-400">—</span>
                  )}
                </td>
                <td className="px-4 py-3 text-right space-x-2 whitespace-nowrap">
                  <button
                    data-testid={`recruit-generate-${p.id}`}
                    onClick={() => generate(p)}
                    disabled={busyId === p.id}
                    className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-[#970C10] hover:text-[#7A0A0D] disabled:opacity-40 transition-colors duration-200"
                  >
                    <Sparkles className="w-3.5 h-3.5" /> {busyId === p.id ? "…" : p.outreach_message ? "Regenerate" : "AI Outreach"}
                  </button>
                  <button data-testid={`recruit-delete-${p.id}`} onClick={() => remove(p)} className="text-slate-400 hover:text-red-600 transition-colors duration-200">
                    <Trash2 className="w-4 h-4 inline" />
                  </button>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400">No prospects yet — paste a list above and let AI write the outreach</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={!!viewing} onOpenChange={(v) => !v && setViewing(null)}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="recruit-message-dialog">
          <DialogHeader><DialogTitle className="font-heading">Outreach for {viewing?.name}</DialogTitle></DialogHeader>
          {viewing && (
            <div className="space-y-3">
              <p className="text-xs text-slate-500">
                {viewing.channel === "email" ? "Email" : viewing.channel === "dm" ? "Social DM" : "WhatsApp"} · generated {viewing.generated_at ? new Date(viewing.generated_at).toLocaleString() : ""}
              </p>
              <textarea readOnly className={`${inputCls} h-56 text-sm leading-relaxed`} value={viewing.outreach_message} data-testid="recruit-message-text" />
              <div className="flex gap-2">
                <button
                  data-testid="recruit-copy-msg-btn"
                  onClick={() => copyMsg(viewing.outreach_message)}
                  className="flex-1 inline-flex items-center justify-center gap-1.5 bg-[#970C10] hover:bg-[#7A0A0D] text-white font-bold text-sm py-2.5 rounded-lg transition-colors duration-200"
                >
                  <Copy className="w-4 h-4" /> Copy message
                </button>
                {waLink(viewing) && (
                  <a
                    data-testid="recruit-wa-send-btn"
                    href={waLink(viewing)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 inline-flex items-center justify-center gap-1.5 bg-green-600 hover:bg-green-700 text-white font-bold text-sm py-2.5 rounded-lg transition-colors duration-200"
                  >
                    <ExternalLink className="w-4 h-4" /> Send on WhatsApp
                  </a>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
